import { Box, DialogContent, Typography, useTheme } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import type { MobileOptionMenuProps } from './__types__/MobileOptionMenu.types';

const MobileOptionMenu = ({ open, onClose, title, options, onSelect, sx }: MobileOptionMenuProps) => {
  const theme = useTheme();
  const [focusedIndex, setFocusedIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setFocusedIndex(0);
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (options.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => (prev + 1) % options.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => (prev - 1 + options.length) % options.length);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          onSelect(options[focusedIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        case 'Tab':
          break;
        default:
          break;
      }
    },
    [focusedIndex, options, onSelect, onClose]
  );

  const handleOptionSelect = useCallback(
    (option: (typeof options)[0]) => {
      onSelect(option);
    },
    [onSelect]
  );

  return (
    <ResponsiveDialog
      fullWidth
      open={open}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      disableEscapeKeyDown={false}
      PaperProps={{
        sx: {
          position: 'fixed',
          bottom: 0,
          m: 0,
          width: '100%',
          maxHeight: '75vh',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          ...sx,
        },
      }}
    >
      {title && (
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>
        </Box>
      )}
      <DialogContent sx={{ p: 0 }} onKeyDown={handleKeyDown} tabIndex={-1} role="listbox" aria-label={title}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {options.map((option, index) => (
            <Box
              key={option.action}
              onClick={() => handleOptionSelect(option)}
              tabIndex={focusedIndex === index ? 0 : -1}
              role="option"
              aria-selected={focusedIndex === index}
              aria-label={option.name}
              sx={{
                p: 2,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: focusedIndex === index ? theme.palette.action.selected : 'transparent',
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                },
                '&:active': {
                  bgcolor: theme.palette.action.selected,
                },
                '&:focus': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: -2,
                },
              }}
            >
              {option.icon}
              <Typography variant="body1">{option.name}</Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </ResponsiveDialog>
  );
};

export default MobileOptionMenu;
